import React, { useState, useEffect, useRef, useCallback } from "react";
import { browserHistory } from "react-router";

import ConfirmationModal from "../../shared/ConfirmationModal";
import PaginatedTable from "../../shared/PaginatedTable";

import ChatApi from "../../../services/resources/ChatApi";
import { ApiCallErrorMessageHandler } from "../../../lib/coc-common-scripts";
import { formatFullName } from "../../../lib";
import axios from "axios";
import debounce from "lodash.debounce";
import Toggle from "../../shared/Toggle";
import { Checkbox, FormControlLabel, IconButton } from "@material-ui/core";
import { Block, Delete, PersonAdd } from "@material-ui/icons";

const sortOptions = [
  { id: "name", name: "Name" },
  { id: "chabadHouseName", name: "Chabad House Name" },
  { id: "campusName", name: "Campus Name" },
];

const MemberFilterOptions = {
  Members: "Members",
  NonMembers: "NonMembers",
  Banned: "Banned",
};

const ActionTypes = {
  Add: "add",
  Remove: "remove",
  Ban: "ban",
  Unban: "unban",
};

const resultsPerPage = 12;

export function ChatStudents({ allowEdit }) {
  // students list state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(sortOptions[0].id);
  const [filters, setFilters] = useState({
    text: "",
    memberFilter: MemberFilterOptions.Members,
  });
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);

  // shluchim action state
  const [actionModal, setActionModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionErrorMessage, setActionErrorMessage] = useState("");
  const [preventAutoEnroll, setPreventAutoEnroll] = useState(false);

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  const getStudents = useCallback(
    async (
      pageNum = 1,
      numResults = resultsPerPage,
      sortByValue = sortBy,
      filtersValue = filters,
    ) => {
      setErrorMessage("");
      setLoading(true);
      setPage(pageNum);
      setSortBy(sortByValue);

      try {
        const { students: studentsData, totalStudents: total } =
          await ChatApi.getStudents(
            pageNum,
            numResults,
            sortByValue,
            filtersValue,
            apiSignalRef.current.token,
          );

        setLoading(false);
        setStudents(studentsData);
        setTotalStudents(total);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setErrorMessage(ApiCallErrorMessageHandler(err));
          setLoading(false);
          setStudents([]);
          setTotalStudents(0);
        }
      }
    },
    [filters, sortBy],
  );

  const filterStudents = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      getStudents(undefined, undefined, undefined, newFilters);
    },
    [getStudents],
  );

  const handleAction = useCallback(async () => {
    if (!actionModal) return;

    setActionErrorMessage("");
    setActionLoading(true);

    try {
      const { action, student } = actionModal;

      if (action === ActionTypes.Add) {
        await ChatApi.addChatMember(
          student.personID,
          apiSignalRef.current.token,
        );
      } else if (action === ActionTypes.Remove) {
        await ChatApi.removeChatMember(
          student.personID,
          apiSignalRef.current.token,
          preventAutoEnroll,
        );
      } else if (action === ActionTypes.Ban) {
        await ChatApi.banChatMember(
          student.personID,
          apiSignalRef.current.token,
        );
      } else if (action === ActionTypes.Unban) {
        await ChatApi.unbanChatMember(
          student.personID,
          apiSignalRef.current.token,
        );
      }

      getStudents(page);
      setActionModal(null);
      setActionLoading(false);
      setPreventAutoEnroll(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setActionErrorMessage(ApiCallErrorMessageHandler(err));
        setActionLoading(false);
      }
    }
  }, [actionModal, getStudents, page, preventAutoEnroll]);

  const getActionMessage = () => {
    if (!actionModal) return "";

    const name = formatFullName(
      actionModal.student.firstName,
      actionModal.student.lastName,
    );

    switch (actionModal.action) {
      case ActionTypes.Add:
        return `Are you sure you'd like to add ${name} to the chat community?`;
      case ActionTypes.Remove:
        return (
          <>
            <p className="mb-16">
              Are you sure you'd like to remove {name} from the chat community?
            </p>
            <FormControlLabel
              className="chat-auto-enroll-checkbox"
              control={
                <Checkbox
                  checked={preventAutoEnroll}
                  onChange={(e) => setPreventAutoEnroll(e.target.checked)}
                />
              }
              label="Prevent student from being auto-enrolled in the future. They can opt in manually from their profile."
            />
          </>
        );
      case ActionTypes.Ban:
        return `Are you sure you'd like to ban ${name} from the chat community? This will ${
          filters.memberFilter === MemberFilterOptions.Members
            ? "remove their membership and prevent them from rejoining"
            : "prevent them from joining"
        } in the future.`;
      case ActionTypes.Unban:
        return `Are you sure you'd like to unban ${name}? They will be able to join the chat community.`;
      default:
        return "";
    }
  };

  return (
    <div>
      <div className="mb-24">
        <p className="xxl-text fw-500">Students</p>
        <p className="accent-text small-text">
          Students must be associated with an enrolled Chabad House location to
          be added to the chat community. Students associated with an enrolled
          Chabad House are automatically added to the chat community when they
          access the student app. They can opt in or out of the chat community
          from their profile. If a student is banned, they are removed from the
          community and cannot opt back in.
        </p>
      </div>
      <div className="chat-students-table">
        <PaginatedTable
          filterComponent={
            <Filters initialFilters={filters} applyFilters={filterStudents} />
          }
          loading={loading}
          loadData={getStudents}
          page={page}
          records={students}
          renderHeader={() => <Header />}
          renderRow={(student, index) => (
            <Row
              allowEdit={allowEdit}
              key={index}
              student={student}
              onAddMember={() =>
                setActionModal({ action: ActionTypes.Add, student })
              }
              onRemoveMember={() =>
                setActionModal({ action: ActionTypes.Remove, student })
              }
              onBan={() => setActionModal({ action: ActionTypes.Ban, student })}
              onUnban={() =>
                setActionModal({ action: ActionTypes.Unban, student })
              }
            />
          )}
          results={resultsPerPage}
          showResultsCount={true}
          sortBy={sortBy}
          sortOptions={sortOptions}
          success={!errorMessage}
          totalCount={totalStudents}
        />
      </div>

      {!!actionModal && (
        <ConfirmationModal
          cancel={() => {
            setActionModal(null);
            setActionErrorMessage("");
            setPreventAutoEnroll(false);
          }}
          confirm={handleAction}
          errorMessage={actionErrorMessage}
          loading={actionLoading}
          message={getActionMessage()}
          show={true}
        />
      )}
    </div>
  );
}

function Filters({ applyFilters, initialFilters }) {
  const [text, setText] = useState(initialFilters.text);
  const [memberFilter, setMemberFilter] = useState(initialFilters.memberFilter);

  const debouncedApplyFilters = useRef(debounce(applyFilters, 500));
  const isInitialMount = useRef(true);

  useEffect(() => {
    // run effect when filters are updated - exclude initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    debouncedApplyFilters.current({ text, memberFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleMemberFilterChange = (name, value) => {
    setMemberFilter(value);
    applyFilters({ text, memberFilter: value });
  };

  return (
    <div className="flex flex-align-center">
      <div className="search-input">
        <input
          type="text"
          placeholder="Search Student name"
          name="text"
          onChange={(event) => setText(event.target.value)}
          value={text}
        />
        <i className="material-icons accent-text-secondary flex flex-align-center">
          search
        </i>
      </div>
      <div className="ml-24">
        <Toggle
          name="memberFilter"
          onChange={handleMemberFilterChange}
          options={[
            { value: MemberFilterOptions.Members, display: "Members" },
            { value: MemberFilterOptions.NonMembers, display: "Non-Members" },
            { value: MemberFilterOptions.Banned, display: "Banned" },
          ]}
          value={memberFilter}
        />
      </div>
    </div>
  );
}

function Header() {
  const columns = ["Name", "Chabad House", "Campus", "Visibility", ""];

  return (
    <div className="chat-students-table-header">
      {columns.map((column, index) => (
        <p className="flex flex-align-center" key={index}>
          {column}
        </p>
      ))}
    </div>
  );
}

function Row({
  allowEdit,
  student,
  onAddMember,
  onRemoveMember,
  onBan,
  onUnban,
}) {
  const {
    studentID,
    firstName,
    lastName,
    campusName,
    chabadHouseName,
    chatVisibilityDisplay,
    isChatMember,
    isBanned,
  } = student;

  return (
    <div
      className="chat-students-table-row"
      onClick={() => browserHistory.push(`/students/${studentID}`)}
    >
      <p>{formatFullName(firstName, lastName)}</p>
      <p>{chabadHouseName}</p>
      <p>{campusName}</p>
      <p>
        {isBanned ? (
          <span className="badge badge-error">Banned</span>
        ) : (
          chatVisibilityDisplay
        )}
      </p>
      <p className="flex">
        {isBanned ? (
          <button
            className="btn custom-btn btn-medium btn-light"
            disabled={!allowEdit}
            onClick={(e) => {
              e.stopPropagation();
              onUnban();
            }}
          >
            Unban
          </button>
        ) : (
          <>
            {isChatMember ? (
              <IconButton
                disabled={!allowEdit}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveMember();
                }}
              >
                <Delete fontSize="large" />
              </IconButton>
            ) : (
              <IconButton
                disabled={!allowEdit}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddMember();
                }}
              >
                <PersonAdd fontSize="large" />
              </IconButton>
            )}
            <IconButton
              disabled={!allowEdit}
              onClick={(e) => {
                e.stopPropagation();
                onBan();
              }}
            >
              <Block fontSize="large" />
            </IconButton>
          </>
        )}
      </p>
    </div>
  );
}
